import { useCallback, useEffect, useState } from 'react'
import { Megaphone, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchAnnouncements,
  createAnnouncement,
  toggleAnnouncement,
  deleteAnnouncement,
  toRFC3339,
  SEVERITIES,
  sevMeta,
  AN_STR as S,
  type Announcement,
} from './announcementsApi'

export function AnnouncementsScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<Announcement[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [severity, setSeverity] = useState('info')
  const [mandatory, setMandatory] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchAnnouncements(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const create = async () => {
    if (!title.trim()) return
    setBusy(true)
    try {
      await createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        severity,
        mandatory,
        starts_at: toRFC3339(from),
        ends_at: toRFC3339(to),
      })
      setTitle('')
      setBody('')
      setMandatory(false)
      setFrom('')
      setTo('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const toggle = async (a: Announcement) => {
    setRows((prev) => prev?.map((x) => (x.id === a.id ? { ...x, enabled: !x.enabled } : x)) ?? prev)
    try {
      await toggleAnnouncement(a.id, !a.enabled)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
      reload()
    }
  }
  const del = async (id: number) => {
    try {
      await deleteAnnouncement(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A15.19" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create */}
          <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={S.name}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={S.body}
              rows={2}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <div className="flex flex-wrap items-center gap-2 text-xs text-tertiary">
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                {SEVERITIES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-secondary">
                <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
                {S.mandatory}
              </label>
              <label>
                {S.from}
                <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} className="ml-1 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-sm text-primary" />
              </label>
              <label>
                {S.to}
                <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} className="ml-1 rounded-md border border-border-default bg-surface-1 px-2 py-1 text-sm text-primary" />
              </label>
              <button type="button" onClick={create} disabled={busy || !title.trim()} className="ml-auto flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
                <Megaphone size={14} />
                {S.add}
              </button>
            </div>
          </div>

          {/* List */}
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((a) => {
                const sev = sevMeta(a.severity)
                return (
                  <div key={a.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-primary">{a.title}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${sev.pill}`}>{sev.label}</span>
                          {a.mandatory && <span className="rounded bg-danger/10 px-1.5 py-0.5 text-xs text-danger">!</span>}
                          {a.live && <span className="text-xs text-success">● {S.live}</span>}
                        </div>
                        {a.body && <p className="mt-1 text-sm text-secondary">{a.body}</p>}
                        {(a.starts_at || a.ends_at) && (
                          <p className="mt-0.5 text-xs text-tertiary">
                            {a.starts_at ? new Date(a.starts_at).toLocaleString() : '—'} → {a.ends_at ? new Date(a.ends_at).toLocaleString() : '∞'}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={a.enabled}
                          onClick={() => toggle(a)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${a.enabled ? 'bg-accent' : 'bg-surface-3'}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${a.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                        <button type="button" onClick={() => del(a.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
